import { useMemo } from 'react'
import { useQuery } from '@apollo/client'
import { useHistory, useLocation } from 'react-router-dom'

import ChipSelect from 'components/ChipSelect'
import { LANGUAGE_FROM_TOLGEE } from 'consts/languageFromTolgee'
import type { TerritoryCategoriesQuery } from 'generated/graphql'
import { useCurrentLanguage, useTranslate } from 'lib/tolgee'
import { analyticsTracker, TOLGEE_GLOBALS } from 'utils'

import { TERRITORY_CATEGORIES_ERROR_LOG } from './categories-filter.consts'
import { TERRITORY_CATEGORIES } from './query'

type availableLanguages = 'por' | 'spa' | 'eng' | 'all'

interface ICategoriesFilter {
  languages?: availableLanguages[]
}

export default function CategoriesFilter({ languages }: ICategoriesFilter) {
  const t = useTranslate()
  const currentLanguage = useCurrentLanguage()
  const location = useLocation()
  const history = useHistory()

  const { data, loading } = useQuery<TerritoryCategoriesQuery>(TERRITORY_CATEGORIES, {
    variables: {
      languages,
    },
    context: { errorLog: TERRITORY_CATEGORIES_ERROR_LOG },
  })

  const categoryOptions = useMemo(() => {
    if (data?.allTopicGroups?.length) {
      const baseCategories = data?.allTopicGroups ?? []
      const formattedCategories = baseCategories.map(item => {
        return {
          label: item?.title,
          value: item?.topicGroupId,
          topics: item?.topics,
          language: item?.language,
        }
      })

      const languagesWithCategories = formattedCategories.reduce(
        (prev, curr) => {
          return {
            ...prev,
            [curr?.language]: {
              ...prev[curr?.language],
              options: [...(prev[curr?.language]?.options ?? []), curr],
            },
          }
        },
        {
          por: {
            label: t(TOLGEE_GLOBALS.por),
            options: [],
          },
          spa: {
            label: t(TOLGEE_GLOBALS.spa),
            options: [],
          },
          eng: {
            label: t(TOLGEE_GLOBALS.eng),
            options: [],
          },
        }
      )

      const prefferedLanguage = LANGUAGE_FROM_TOLGEE[currentLanguage]
      const availableKeys = Object.keys(languagesWithCategories)
      const prefferedLanguageIndex = availableKeys.indexOf(prefferedLanguage)

      const isNotFound = -1
      const isFirstInArray = 0

      if (![isNotFound, isFirstInArray].includes(prefferedLanguageIndex)) {
        availableKeys[prefferedLanguageIndex] = availableKeys[0]
        availableKeys[0] = prefferedLanguage
      }

      const orderedOptions = availableKeys.map(item => languagesWithCategories[item])

      return [{ label: t(TOLGEE_GLOBALS.allTerritories), value: null }, ...orderedOptions]
    }
    return []
  }, [currentLanguage, data?.allTopicGroups, t])

  const handleChange = value => {
    const newSearchParams = new URLSearchParams(location.search)
    if (value?.value) {
      newSearchParams.set('categories', String(value?.value))
    } else {
      newSearchParams.delete('categories')
    }

    // if the topics filter is active it should be reset
    newSearchParams.delete('topics')

    analyticsTracker.eventTrack(analyticsTracker.eventNames.TOP.FILTER_CATEGORY, {
      selectedCategory: value?.label,
    })

    const filterSearchParams = newSearchParams.toString()
    history.push({ search: filterSearchParams })
  }

  const currentValue = useMemo(() => {
    const searchParams = new URLSearchParams(location.search)

    const categoriesValue = searchParams.get('categories')

    const baseCategories = data?.allTopicGroups ?? []
    const formattedCategories = baseCategories.map(item => {
      return {
        label: item?.title,
        value: String(item?.topicGroupId),
      }
    })

    const currentCategory = formattedCategories.find(item => item.value === categoriesValue)
    return currentCategory
  }, [data?.allTopicGroups, location.search])

  return (
    <ChipSelect
      onSelect={handleChange}
      selectedValue={currentValue}
      options={categoryOptions}
      isDisabled={loading}
      label={t(TOLGEE_GLOBALS.allTerritories)}
    />
  )
}
